import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Stack,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CurrencyExchange as RefundIcon,
  DeleteOutline as DeleteIcon,
  CheckCircleOutline as MarkPaidIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useCanManageLeague, useIsSuperAdmin } from '../../../contexts/PermissionsContext';

const POSITION_LABELS = {
  OutfieldPlayer: 'Outfield Player',
  Goalkeeper: 'Goalkeeper',
  SpareOutfielder: 'Spare Outfielder',
  SpareGoalkeeper: 'Spare Goalkeeper',
};

const STATUS_LABELS = {
  Pending: 'Pending',
  Paid: 'Paid',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
  Refunded: 'Refunded',
};

const STATUS_COLORS = {
  Pending: 'warning',
  Paid: 'success',
  Expired: 'default',
  Cancelled: 'error',
  Refunded: 'info',
};

const RegistrationDashboardPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const canManageLeague = useCanManageLeague(parseInt(leagueId));
  const isSuperAdmin = useIsSuperAdmin();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [inventory, setInventory] = useState(null);

  // Filters
  const [positionFilter, setPositionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [newToLeagueFilter, setNewToLeagueFilter] = useState('');

  // Detail dialog
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Refund dialog
  const [refundTarget, setRefundTarget] = useState(null);
  const [removeMembership, setRemoveMembership] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [deleteExpiredTarget, setDeleteExpiredTarget] = useState(null);
  const [deleteExpiredLoading, setDeleteExpiredLoading] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ positionType: '', preferredPosition1: '', preferredPosition2: '', amountPaid: '', status: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Search
  const [searchText, setSearchText] = useState('');

  // CSV export
  const [exportLoading, setExportLoading] = useState(false);

  // Sort state
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const filteredRegistrations = useMemo(() => {
    if (!searchText.trim()) return registrations;
    const term = searchText.toLowerCase();
    return registrations.filter(reg => {
      const name = `${reg.firstName} ${reg.lastName}`.toLowerCase();
      const email = (reg.email || '').toLowerCase();
      const position = (POSITION_LABELS[reg.positionType] || '').toLowerCase();
      const status = (STATUS_LABELS[reg.status] || '').toLowerCase();
      return name.includes(term) || email.includes(term) || position.includes(term) || status.includes(term);
    });
  }, [registrations, searchText]);

  const sortedRegistrations = useMemo(() => {
    const comparator = (a, b) => {
      let aVal, bVal;
      if (orderBy === 'name') {
        aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
        bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
      } else if (orderBy === 'positionType') {
        aVal = (POSITION_LABELS[a.positionType] || '').toLowerCase();
        bVal = (POSITION_LABELS[b.positionType] || '').toLowerCase();
      } else if (orderBy === 'status') {
        aVal = (STATUS_LABELS[a.status] || '').toLowerCase();
        bVal = (STATUS_LABELS[b.status] || '').toLowerCase();
      } else if (orderBy === 'isNewToLeague') {
        aVal = a.isNewToLeague ? 1 : 0;
        bVal = b.isNewToLeague ? 1 : 0;
      } else {
        aVal = a[orderBy] ?? '';
        bVal = b[orderBy] ?? '';
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    };
    return [...filteredRegistrations].sort(comparator);
  }, [filteredRegistrations, orderBy, order]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (positionFilter) filters.positionType = positionFilter;
      if (statusFilter) filters.status = statusFilter;
      if (newToLeagueFilter) filters.isNewToLeague = newToLeagueFilter;

      const [regs, inv] = await Promise.all([
        unifiedApi.getRegistrations(leagueId, filters),
        unifiedApi.getRegistrationInventory(leagueId),
      ]);
      setRegistrations(regs || []);
      setInventory(inv);
    } catch (err) {
      setError('Failed to load registration data.');
    } finally {
      setLoading(false);
    }
  }, [leagueId, positionFilter, statusFilter, newToLeagueFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetails = async (regId) => {
    setDetailLoading(true);
    try {
      const detail = await unifiedApi.getRegistrationById(leagueId, regId);
      if (!detail) {
        setError('Registration not found — it may have been deleted.');
        return;
      }
      setSelectedRegistration(detail);
    } catch {
      setError('Failed to load registration details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  // Installment due dates arrive as tenant-local wall-clock (no offset). Parse the date components
  // directly so they are not reinterpreted in the administrator's browser timezone.
  const formatTenantLocalDate = (dateStr) => {
    if (!dateStr) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (!m) return dateStr;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      const data = await unifiedApi.getRegistrationsExport(leagueId);
      if (!data || data.length === 0) {
        setError('No paid registrations to export.');
        return;
      }

      const escCsv = (val) => {
        if (val == null) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        'Registration ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Position',
        'Status', 'Birthday', 'Postal Code', 'Jersey Size', 'Preferred Position 1',
        'Preferred Position 2', 'Expected Games to Miss', 'New to League',
        'Referred By', 'Waiver Accepted', 'Expires At', 'Registered At',
        'Payment Method', 'Total', 'Amount Paid', 'Remaining Balance', 'Plan Status'
      ];

      const rows = data.map(reg => [
        reg.id,
        reg.firstName,
        reg.lastName,
        reg.email,
        reg.phone,
        POSITION_LABELS[reg.positionType] || reg.positionType,
        STATUS_LABELS[reg.status] || reg.status,
        reg.birthday || '',
        reg.postalCode,
        reg.jerseySize,
        reg.preferredPosition1,
        reg.preferredPosition2,
        reg.expectedGamesToMiss,
        reg.isNewToLeague ? 'Yes' : 'No',
        reg.referral,
        reg.waiverAcceptedAt || '',
        reg.expiresAt || '',
        reg.createdAt || '',
        reg.installmentPlan ? 'Installments' : 'Full',
        reg.installmentPlan ? formatPrice(reg.installmentPlan.totalAmountCents) : '',
        reg.installmentPlan ? formatPrice(reg.installmentPlan.amountPaidCents) : '',
        reg.installmentPlan ? formatPrice(reg.installmentPlan.remainingBalanceCents) : '',
        reg.installmentPlan ? reg.installmentPlan.status : '',
      ]);

      const csv = [headers, ...rows].map(row => row.map(escCsv).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `registrations-paid-league-${leagueId}.csv`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }, 0);
    } catch {
      setError('Failed to export registrations.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefundLoading(true);
    try {
      await unifiedApi.refundRegistration(leagueId, refundTarget.id, removeMembership);
      setRefundTarget(null);
      setRemoveMembership(false);
      fetchData();
    } catch {
      setError('Failed to process refund.');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleMarkExpiredAsPaid = async () => {
    if (!markPaidTarget) return;
    setMarkPaidLoading(true);
    try {
      await unifiedApi.markExpiredRegistrationPaid(leagueId, markPaidTarget.id);
      setMarkPaidTarget(null);
      fetchData();
    } catch {
      setError('Failed to mark registration as paid.');
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const handleDeleteExpiredRegistration = async () => {
    if (!deleteExpiredTarget) return;
    setDeleteExpiredLoading(true);
    try {
      await unifiedApi.deleteExpiredRegistration(leagueId, deleteExpiredTarget.id);
      setDeleteExpiredTarget(null);
      fetchData();
    } catch {
      setError('Failed to delete expired registration.');
    } finally {
      setDeleteExpiredLoading(false);
    }
  };

  const handleOpenEdit = async (reg) => {
    setEditError(null);
    // Fetch full detail so preferred positions and the amount-paid override are accurate.
    // The list row omits those fields, so opening the editor with it would blank them out
    // and a save could clear stored values — abort if full detail cannot be loaded.
    let detail;
    try {
      detail = await unifiedApi.getRegistrationById(leagueId, reg.id);
    } catch {
      setError('Failed to load registration details for editing. Please try again.');
      return;
    }
    if (!detail) {
      setError('Registration not found — it may have been deleted.');
      return;
    }
    setEditForm({
      positionType: detail.positionType || '',
      preferredPosition1: detail.preferredPosition1 || '',
      preferredPosition2: detail.preferredPosition2 || '',
      amountPaid: detail.amountPaidCentsOverride != null ? (detail.amountPaidCentsOverride / 100).toString() : '',
      status: detail.status || '',
    });
    setEditTarget(detail);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditError(null);

    let amountPaidCentsOverride = null;
    const trimmed = (editForm.amountPaid ?? '').toString().trim();
    if (trimmed !== '') {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setEditError('Amount paid must be a non-negative number.');
        return;
      }
      amountPaidCentsOverride = Math.round(parsed * 100);
    }

    setEditLoading(true);
    try {
      await unifiedApi.updateRegistration(leagueId, editTarget.id, {
        positionType: editForm.positionType,
        preferredPosition1: editForm.preferredPosition1 || null,
        preferredPosition2: editForm.preferredPosition2 || null,
        amountPaidCentsOverride,
        status: editForm.status || null,
      });
      setEditTarget(null);
      fetchData();
    } catch (err) {
      setEditError(err?.message || 'Failed to update registration.');
    } finally {
      setEditLoading(false);
    }
  };

  const renderRowActions = (reg) => (
    <>
      <Button size="small" onClick={() => handleViewDetails(reg.id)}>
        View
      </Button>
      {canManageLeague && (
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleOpenEdit(reg)}
        >
          Edit
        </Button>
      )}
      {canManageLeague && reg.status === 'Paid' && (
        <Button
          size="small"
          color="info"
          startIcon={<RefundIcon />}
          onClick={() => setRefundTarget(reg)}
        >
          Refund
        </Button>
      )}
      {isSuperAdmin && reg.status === 'Expired' && (
        <>
          <Button
            size="small"
            color="success"
            startIcon={<MarkPaidIcon />}
            onClick={() => setMarkPaidTarget(reg)}
          >
            Mark Paid
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteExpiredTarget(reg)}
          >
            Delete
          </Button>
        </>
      )}
    </>
  );

  const renderRegistrationName = (reg) => (
    reg.userTenantId ? (
      <Link to={`/player/${reg.userTenantId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Typography variant="body2" component="span" sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
          {reg.firstName} {reg.lastName}
        </Typography>
      </Link>
    ) : (
      `${reg.firstName} ${reg.lastName}`
    )
  );

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Registration Dashboard</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={exportLoading || loading}
              variant="outlined"
            >
              Export Paid to CSV
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Inventory Summary */}
      {inventory && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Inventory Summary</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="h4">{inventory.totalRegistrations}</Typography>
                <Typography variant="body2" color="text.secondary">Total</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="h4" color="success.main">{inventory.paidRegistrations}</Typography>
                <Typography variant="body2" color="text.secondary">Paid</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h4" color="warning.main">{inventory.pendingRegistrations}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" color="info.main">{inventory.refundedRegistrations || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Refunded</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" color="info.main">{formatPrice(inventory.totalPaymentsInCents || 0)}</Typography>
                <Typography variant="body2" color="text.secondary">Payments Collected</Typography>
              </Box>
            </Grid>
          </Grid>

          {inventory.positions?.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell align="right">Filled</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Available</TableCell>
                    <TableCell align="right">Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory.positions.map(pos => (
                    <TableRow key={pos.positionType}>
                      <TableCell>{POSITION_LABELS[pos.positionType]}</TableCell>
                      <TableCell align="right">{pos.filledSlots}</TableCell>
                      <TableCell align="right">{pos.totalSlots}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={pos.availableSlots}
                          size="small"
                          color={pos.availableSlots === 0 ? 'error' : 'success'}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell align="right">{formatPrice(pos.priceInCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search registrations..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 220 }}
            inputProps={{ 'aria-label': 'Search registrations' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Position</InputLabel>
            <Select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} label="Position">
              <MenuItem value="">All</MenuItem>
              {Object.entries(POSITION_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>New to League</InputLabel>
            <Select value={newToLeagueFilter} onChange={(e) => setNewToLeagueFilter(e.target.value)} label="New to League">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Registrations Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        sortedRegistrations.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No registrations found.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {sortedRegistrations.map(reg => (
              <Paper key={reg.id} sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {renderRegistrationName(reg)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{reg.id} · {reg.email}
                      </Typography>
                    </Box>
                    <Chip
                      label={STATUS_LABELS[reg.status]}
                      size="small"
                      color={STATUS_COLORS[reg.status]}
                      clickable={false}
                      onClick={() => {}}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip label={POSITION_LABELS[reg.positionType]} size="small" variant="outlined" clickable={false} onClick={() => {}} />
                    {reg.paymentMethod === 'Installments' ? (
                      <Chip
                        label={`Installments · ${formatPrice(reg.remainingBalanceCents || 0)} left`}
                        size="small"
                        color="info"
                        variant="outlined"
                        clickable={false}
                        onClick={() => {}}
                      />
                    ) : (
                      <Chip label="Full" size="small" variant="outlined" clickable={false} onClick={() => {}} />
                    )}
                    <Chip
                      label={reg.isNewToLeague ? 'New to League' : 'Returning'}
                      size="small"
                      variant="outlined"
                      color={reg.isNewToLeague ? 'success' : 'default'}
                      clickable={false}
                      onClick={() => {}}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Registered {formatDate(reg.createdAt)}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {renderRowActions(reg)}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'id' ? order : false}>
                    <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleSort('id')}>ID</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'name' ? order : false}>
                    <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>Name</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'email' ? order : false}>
                    <TableSortLabel active={orderBy === 'email'} direction={orderBy === 'email' ? order : 'asc'} onClick={() => handleSort('email')}>Email</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'positionType' ? order : false}>
                    <TableSortLabel active={orderBy === 'positionType'} direction={orderBy === 'positionType' ? order : 'asc'} onClick={() => handleSort('positionType')}>Position</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell sortDirection={orderBy === 'isNewToLeague' ? order : false}>
                    <TableSortLabel active={orderBy === 'isNewToLeague'} direction={orderBy === 'isNewToLeague' ? order : 'asc'} onClick={() => handleSort('isNewToLeague')}>New to League</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                    <TableSortLabel active={orderBy === 'createdAt'} direction={orderBy === 'createdAt' ? order : 'asc'} onClick={() => handleSort('createdAt')}>Registered</TableSortLabel>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRegistrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No registrations found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRegistrations.map(reg => (
                    <TableRow key={reg.id} hover>
                      <TableCell>{reg.id}</TableCell>
                      <TableCell>
                        {renderRegistrationName(reg)}
                      </TableCell>
                      <TableCell>{reg.email}</TableCell>
                      <TableCell>{POSITION_LABELS[reg.positionType]}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[reg.status]}
                          size="small"
                          color={STATUS_COLORS[reg.status]}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell>
                        {reg.paymentMethod === 'Installments' ? (
                          <>
                            <Chip label="Installments" size="small" color="info" variant="outlined" clickable={false} onClick={() => {}} />
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatPrice(reg.remainingBalanceCents || 0)} left
                            </Typography>
                          </>
                        ) : (
                          <Chip label="Full" size="small" variant="outlined" clickable={false} onClick={() => {}} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reg.isNewToLeague ? 'Yes' : 'No'}
                          size="small"
                          variant="outlined"
                          color={reg.isNewToLeague ? 'success' : 'default'}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell>{formatDate(reg.createdAt)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {renderRowActions(reg)}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Registration Detail Dialog */}
      <Dialog
        open={!!selectedRegistration}
        onClose={() => setSelectedRegistration(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedRegistration && (
          <>
            <DialogTitle>
              Registration Details — {selectedRegistration.firstName} {selectedRegistration.lastName}
            </DialogTitle>
            <DialogContent dividers>
              {detailLoading ? (
                <CircularProgress />
              ) : (
                <Stack spacing={1.5}>
                  <DetailRow label="Registration ID" value={selectedRegistration.id} />
                  <DetailRow label="Email" value={selectedRegistration.email} />
                  <DetailRow label="Phone" value={selectedRegistration.phone} />
                  <DetailRow label="Position" value={POSITION_LABELS[selectedRegistration.positionType]} />
                  <DetailRow label="Status" value={STATUS_LABELS[selectedRegistration.status]} />
                  <DetailRow label="Birthday" value={selectedRegistration.birthday ? new Date(selectedRegistration.birthday).toLocaleDateString() : '—'} />
                  <DetailRow label="Postal Code" value={selectedRegistration.postalCode} />
                  <DetailRow label="Jersey Size" value={selectedRegistration.jerseySize} />
                  <DetailRow label="Preferred Position 1" value={selectedRegistration.preferredPosition1} />
                  <DetailRow label="Preferred Position 2" value={selectedRegistration.preferredPosition2} />
                  <DetailRow label="Expected Games to Miss" value={selectedRegistration.expectedGamesToMiss} />
                  <DetailRow label="New to League" value={selectedRegistration.isNewToLeague ? 'Yes' : 'No'} />
                  {selectedRegistration.referral && (
                    <DetailRow label="Referred by" value={selectedRegistration.referral} />
                  )}
                  <DetailRow label="Waiver Accepted" value={formatDate(selectedRegistration.waiverAcceptedAt)} />
                  <DetailRow label="Expires At" value={formatDate(selectedRegistration.expiresAt)} />
                  <DetailRow label="Registered At" value={formatDate(selectedRegistration.createdAt)} />
                  {selectedRegistration.amountPaidCentsOverride != null && (
                    <DetailRow label="Amount Paid (override)" value={formatPrice(selectedRegistration.amountPaidCentsOverride)} />
                  )}

                  {selectedRegistration.installmentPlan && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Installment Plan — {selectedRegistration.installmentPlan.status}
                      </Typography>
                      <DetailRow label="Total" value={formatPrice(selectedRegistration.installmentPlan.totalAmountCents)} />
                      <DetailRow label="Paid" value={formatPrice(selectedRegistration.installmentPlan.amountPaidCents)} />
                      <DetailRow label="Remaining" value={formatPrice(selectedRegistration.installmentPlan.remainingBalanceCents)} />
                      <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Due</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRegistration.installmentPlan.installments.map(inst => (
                            <TableRow key={inst.installmentNumber}>
                              <TableCell>{inst.installmentNumber}</TableCell>
                              <TableCell align="right">{formatPrice(inst.amountCents)}</TableCell>
                              <TableCell>{formatTenantLocalDate(inst.dueDate)}</TableCell>
                              <TableCell>{inst.status}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedRegistration(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Refund Confirmation Dialog */}
      <Dialog
        open={!!refundTarget}
        onClose={() => { setRefundTarget(null); setRemoveMembership(false); }}
        maxWidth="sm"
        fullWidth
      >
        {refundTarget && (
          <>
            <DialogTitle>Mark Registration as Refunded</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to mark the registration for{' '}
                <strong>{refundTarget.firstName} {refundTarget.lastName}</strong> ({refundTarget.email}) as refunded?
                This will free their position slot.
              </DialogContentText>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removeMembership}
                    onChange={(e) => setRemoveMembership(e.target.checked)}
                  />
                }
                label="Also remove player's league membership"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Check this for full refunds where the player is leaving. Leave unchecked for partial refunds or players moving to spares.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setRefundTarget(null); setRemoveMembership(false); }}>
                Cancel
              </Button>
              <Button
                onClick={handleRefund}
                color="info"
                variant="contained"
                disabled={refundLoading}
                startIcon={refundLoading ? <CircularProgress size={16} /> : <RefundIcon />}
              >
                {refundLoading ? 'Processing…' : 'Mark as Refunded'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Mark Expired as Paid Dialog */}
      <Dialog
        open={!!markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {markPaidTarget && (
          <>
            <DialogTitle>Mark Expired Registration as Paid</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to mark the expired registration for{' '}
                <strong>{markPaidTarget.firstName} {markPaidTarget.lastName}</strong> ({markPaidTarget.email}) as paid?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMarkPaidTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleMarkExpiredAsPaid}
                color="success"
                variant="contained"
                disabled={markPaidLoading}
                startIcon={markPaidLoading ? <CircularProgress size={16} /> : <MarkPaidIcon />}
              >
                {markPaidLoading ? 'Processing…' : 'Mark as Paid'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Expired Registration Dialog */}
      <Dialog
        open={!!deleteExpiredTarget}
        onClose={() => setDeleteExpiredTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {deleteExpiredTarget && (
          <>
            <DialogTitle>Delete Expired Registration</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to permanently delete the expired registration for{' '}
                <strong>{deleteExpiredTarget.firstName} {deleteExpiredTarget.lastName}</strong> ({deleteExpiredTarget.email})?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteExpiredTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteExpiredRegistration}
                color="error"
                variant="contained"
                disabled={deleteExpiredLoading}
                startIcon={deleteExpiredLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {deleteExpiredLoading ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Edit Registration Dialog */}
      <Dialog
        open={!!editTarget}
        onClose={() => { if (!editLoading) { setEditTarget(null); setEditError(null); } }}
        maxWidth="sm"
        fullWidth
      >
        {editTarget && (
          <>
            <DialogTitle>
              Edit Registration — {editTarget.firstName} {editTarget.lastName}
            </DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ mb: 2 }}>
                Change the registration type, status, verify preferred positions, or override the amount paid.
                Changing the status is a bookkeeping action only and does not trigger any Square payment or refund.
                Changing the type can exceed configured position slots. The player and organization will be emailed the changes.
              </DialogContentText>
              {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="edit-position-type-label">Registration Type</InputLabel>
                  <Select
                    labelId="edit-position-type-label"
                    label="Registration Type"
                    value={editForm.positionType}
                    onChange={(e) => setEditForm(f => ({ ...f, positionType: e.target.value }))}
                  >
                    {Object.entries(POSITION_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="edit-status-label">Status</InputLabel>
                  <Select
                    labelId="edit-status-label"
                    label="Status"
                    value={editForm.status}
                    onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                  >
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Preferred Position 1"
                  fullWidth
                  value={editForm.preferredPosition1}
                  onChange={(e) => setEditForm(f => ({ ...f, preferredPosition1: e.target.value }))}
                />
                <TextField
                  label="Preferred Position 2"
                  fullWidth
                  value={editForm.preferredPosition2}
                  onChange={(e) => setEditForm(f => ({ ...f, preferredPosition2: e.target.value }))}
                />
                <TextField
                  label="Amount Paid"
                  type="number"
                  fullWidth
                  value={editForm.amountPaid}
                  onChange={(e) => setEditForm(f => ({ ...f, amountPaid: e.target.value }))}
                  helperText="Overrides the configured position price. Leave blank to use the default price."
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setEditTarget(null); setEditError(null); }} disabled={editLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleEditSave}
                variant="contained"
                disabled={editLoading || !editForm.positionType || !editForm.status}
                startIcon={editLoading ? <CircularProgress size={16} /> : <EditIcon />}
              >
                {editLoading ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </AdminLayout>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 180 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

export default RegistrationDashboardPage;
